/* eslint-disable */
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast, Toaster } from "sonner";

const CATEGORIES = [
  { key: "wifi", label: "WiFi Quality" },
  { key: "noise", label: "Noise Level" },
  { key: "power", label: "Power Accessibility" },
  { key: "comfort", label: "Comfort" },
  { key: "professionalism", label: "Professionalism" },
  { key: "meeting_friendly", label: "Meeting Friendly" },
  { key: "deep_work_friendly", label: "Deep Work Friendly" },
  { key: "networking_potential", label: "Networking Potential" },
];

function Stars({ value, onChange, name }) {
  return (
    <div className="review-stars" data-testid={`stars-${name}`}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className={"review-star" + (n <= value ? " filled" : "")}
          data-testid={`star-${name}-${n}`}
          aria-label={`${n} out of 5`}>
          ★
        </button>
      ))}
    </div>
  );
}

export default function ReviewForm() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [pending, setPending] = useState(null);
  const [workIntents, setWorkIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overall, setOverall] = useState(0);
  const [text, setText] = useState("");
  const [workIntentDone, setWorkIntentDone] = useState("");
  const [cats, setCats] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/me/pending-reviews"),
      api.get("/options/work-intents"),
    ]).then(([pr, wi]) => {
      const match = pr.data.find(p => p.booking_id === bookingId) || null;
      setPending(match);
      setWorkIntents(wi.data);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [bookingId]);

  const setCat = (k, v) => setCats(c => ({ ...c, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (overall < 1) {
      toast.error("Please give an overall rating");
      return;
    }
    setBusy(true);
    try {
      await api.post("/reviews", {
        booking_id: bookingId,
        overall_rating: overall,
        review_text: text.trim() || undefined,
        work_intent_done: workIntentDone || undefined,
        categories: cats,
      });
      toast.success("Thank you, your review is in.");
      nav("/profile");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <AppShell><div className="m-page-pad"><div className="m-loading-inline">Loading…</div></div></AppShell>;
  if (!pending) {
    return (
      <AppShell>
        <Toaster richColors position="top-center" />
        <div className="m-page-pad">
          <h1 className="m-h1">Review <em>not available</em>.</h1>
          <p className="m-lede">This booking isn&apos;t eligible for review yet, or you&apos;ve already reviewed it.</p>
          <Link to="/profile" className="m-btn-gold" style={{textDecoration:"none"}}>Back to profile →</Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="m-page-pad review-page">
        <span className="m-eyebrow">Métier · Post-booking feedback</span>
        <h1 className="m-h1">Rate your <em>{pending.workspace_name}</em> stay.</h1>
        <p className="m-lede" style={{maxWidth:560}}>
          Your honest feedback helps other Métier members find the right room for the right work. Booked on {pending.date}.
        </p>

        <form className="review-form" onSubmit={onSubmit} data-testid="review-form">
          <section className="review-section">
            <span className="m-eyebrow">Overall</span>
            <h3 className="rec-h3">How was the room?</h3>
            <Stars value={overall} onChange={setOverall} name="overall" />
          </section>

          <section className="review-section">
            <span className="m-eyebrow">Why were you there?</span>
            <h3 className="rec-h3">What kind of work did you do today?</h3>
            <div className="review-intent-grid">
              {workIntents.map(w => (
                <button key={w.key} type="button"
                  className={"review-intent-chip" + (workIntentDone === w.key ? " active" : "")}
                  onClick={() => setWorkIntentDone(w.key === workIntentDone ? "" : w.key)}
                  data-testid={`review-intent-${w.key}`}>
                  {w.label}
                </button>
              ))}
            </div>
          </section>

          <section className="review-section">
            <span className="m-eyebrow">The details</span>
            <h3 className="rec-h3">Rate the room on each:</h3>
            <div className="review-cats">
              {CATEGORIES.map(c => (
                <div className="review-cat-row" key={c.key} data-testid={`review-cat-${c.key}`}>
                  <span className="review-cat-label">{c.label}</span>
                  <Stars value={cats[c.key] || 0} onChange={(v) => setCat(c.key, v)} name={c.key} />
                </div>
              ))}
            </div>
          </section>

          <section className="review-section">
            <span className="m-eyebrow">Anything else? (optional)</span>
            <textarea
              value={text} onChange={e => setText(e.target.value)} rows={4} maxLength={1500}
              placeholder="A line or two about the room, the host, or who you'd recommend it to."
              data-testid="review-text"
              style={{ padding:"14px 16px", border:"1px solid var(--m-border)", borderRadius:9, background:"var(--cream)", fontSize:14, resize:"vertical", color:"var(--m-text)", fontFamily:"'Jost', sans-serif", width:"100%" }}
            />
          </section>

          <div style={{ display:"flex", gap:12, marginTop:8 }}>
            <button type="submit" className="m-btn-gold" disabled={busy || overall < 1} data-testid="review-submit">
              {busy ? "Submitting…" : "Submit review"}
            </button>
            <Link to="/profile" className="m-btn-ghost" data-testid="review-cancel" style={{textDecoration:"none"}}>Cancel</Link>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
